import React from "react";
import { Link } from "react-router-dom";
import { Copy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  MyPartnerStatus,
  myShareLink,
  myTermsLink,
} from "../../features/referrals/models/MyPartnerStatus";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";

const formatMoney = (minorUnits: number, currency: string, locale: string): string =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: minorUnits % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(minorUnits / 100);

interface ReferralPartnerPanelProps {
  status: MyPartnerStatus;
  applying: boolean;
  onApply: () => void;
}

/**
 * The instructor's own partner state: apply → accept the terms → share the
 * link and watch the bounties accrue.
 */
const ReferralPartnerPanel: React.FC<ReferralPartnerPanelProps> = ({
  status,
  applying,
  onApply,
}) => {
  const { t } = useTranslation();
  const shareLink = myShareLink(status);
  const termsLink = myTermsLink(status);

  const copyShareLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success(t("referrals.program.linkCopied"));
    } catch {
      toast.error(t("referrals.program.copyFailed"));
    }
  };

  if (!status.isPartner) {
    return (
      <div className="with-border rounded-2xl p-4 lg:p-6">
        <p className="text-grey-30 dark:text-grey-70">{t("referrals.program.applyHint")}</p>
        <button
          type="button"
          className="btn btn-primary mt-4"
          disabled={applying}
          onClick={onApply}
        >
          {applying ? t("referrals.program.applying") : t("referrals.program.apply")}
        </button>
      </div>
    );
  }

  if (!status.termsAccepted) {
    return (
      <div className="with-border rounded-2xl p-4 lg:p-6">
        <p className="text-grey-30 dark:text-grey-70">{t("referrals.program.termsPending")}</p>
        {termsLink && (
          <Link to={termsLink} className="btn btn-primary mt-4 inline-block">
            {t("referrals.program.reviewTerms")}
          </Link>
        )}
      </div>
    );
  }

  const stats = [
    { label: t("referrals.program.statReferred"), value: `${status.referralCount}` },
    { label: t("referrals.program.statConverted"), value: `${status.convertedCount}` },
    {
      label: t("referrals.program.statUnpaid"),
      value: formatMoney(status.unpaidAmountMinorUnits, status.bountyCurrency, getDisplayLocale()),
    },
    {
      label: t("referrals.program.statPaid"),
      value: formatMoney(status.paidAmountMinorUnits, status.bountyCurrency, getDisplayLocale()),
    },
  ];

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <p className="text-sm font-medium text-grey-30 dark:text-grey-70">
        {t("referrals.program.yourLink")}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <code className="rounded-lg bg-grey-95 px-3 py-2 text-sm text-grey-20 dark:bg-primary-20 dark:text-grey-80">
          {shareLink}
        </code>
        <button
          type="button"
          className="btn btn-secondary flex items-center gap-1.5"
          onClick={copyShareLink}
        >
          <Copy size={16} />
          {t("referrals.program.copyLink")}
        </button>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-2xl bg-grey-95 p-4 dark:bg-primary-20">
            <p className="text-sm text-grey-50 dark:text-grey-70">{stat.label}</p>
            <p className="mt-1 text-2xl font-bold text-grey-10 dark:text-white">{stat.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReferralPartnerPanel;
